/*!
 * Auswara Plate Builder v1.7
 * Product builder for medical ID bracelets that share the oval 37 x 15.5 mm plate
 * (Lustre Bloom, Lustre Black Bloom, Lustre Purple Bloom, Lustre Heart, Lumine Grace).
 * Steps: colour, size, engraving with a live mm-accurate preview of the back of the plate,
 * font, extras (gift packaging, wallet card). Based on the FlexGuard builder.
 * Vanilla JS, no dependencies. Uses the Shopify Ajax Cart API.
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ *
   * Constants (physical sizes in millimetres)
   * ------------------------------------------------------------------ */
  var U = 10;                                   // SVG user units per mm
  var STAGE = { w: 46, h: 22 };                 // preview frame under the photo
  // Blank plate photos (apb-plate-<colour>.png) have a little transparent margin
  // around the plate: this much of the image width / height on each side.
  var IMG_INSET = { x: 0.014, y: 0.029 };
  // Slot geometry, as a share of the plate: flat inner edge in from each end, and the D size
  var SLOT = { edge: 0.127, rx: 0.0775, ry: 0.173 };
  var SVGNS = 'http://www.w3.org/2000/svg';
  var XMLNS = 'http://www.w3.org/XML/1998/namespace';

  // Preview faces. The CSS families are declared with @font-face in the
  // section: the real font first via local(), then a free same-width twin.
  var FACES = {
    arial:   { css: '"FG Arial"',   stack: '"FG Arial", Arial, Arimo, Helvetica, sans-serif' },
    century: { css: '"FG Century"', stack: '"FG Century", Century, "Century Schoolbook", serif' },
    times:   { css: '"FG Times"',   stack: '"FG Times", "Times New Roman", Tinos, Times, serif' },
    comic:   { css: '"FG Comic"',   stack: '"FG Comic", "Comic Sans MS", cursive' },
    mvboli:  { css: '"FG MV Boli"', stack: '"FG MV Boli", "MV Boli", cursive',
               local: ['MV Boli', 'MVBoli'], twinStroke: 0.024, twinNote: true }
  };

  var FALLBACK_COLOURS = {
    silver:      { light: '#f8f9fa', dark: '#a6abb1', text: '#4a4e54' },
    gold:        { light: '#faecc0', dark: '#bf994e', text: '#6f5a2c' },
    'rose gold': { light: '#f8e4d6', dark: '#c99a80', text: '#825f48' },
    black:       { light: '#4a4b4f', dark: '#141416', text: '#e6e7e9' }
  };

  var T = {
    colourTitle: 'Colour',
    sizeTitle: 'Size',
    sizeGuide: 'How to measure',
    guideTitle: 'Find your size',
    guideClose: 'Close',
    galleryPrev: 'Previous photo',
    galleryNext: 'Next photo',
    galleryZoom: 'Zoom in on the photo',
    thumbsPrev: 'Scroll photos back',
    thumbsNext: 'Scroll photos forward',
    viewerHint: 'Tap the photo to zoom in',
    sizeGuideHide: 'Hide sizing help',
    guideBestBadge: 'Most accurate',
    guideBestTitle: 'Measure a bracelet or watch you already wear',
    guideBestText: 'Pick one that fits you comfortably. For a bracelet, lay it flat and measure from end to end, including the clasp. For a watch, measure from the hole you use to the end of the buckle, including the watch face. Choose the size closest to that length.',
    guideWristTitle: 'No bracelet handy? Measure your wrist',
    guideCalcTitle: 'Get a size suggestion',
    guideModeBracelet: 'I measured a bracelet or watch',
    guideModeWrist: 'I measured my wrist',
    guideLenLabel: 'Its length, end to end',
    guideWristLabel: 'Your wrist measures',
    guideSuggestLen: 'We suggest {size}, the closest to the bracelet you measured.',
    guideSuggestWrist: 'We suggest {size}, your wrist plus 1 to 2 cm for a comfortable fit.',
    guideTooBig: 'Our largest size is {size}, which may be snug. Message us before ordering and we\u2019ll help.',
    guideTooSmall: 'Our smallest size is {size}, so it may sit a little loose on you.',
    guideChoose: 'Choose {size}',
    guideSizes: 'This bracelet comes in {list}.',
    guideBetween: 'Between sizes? Most people prefer the larger one.',
    guideArtText: 'End to end, including the clasp',
    guideArtLabel: 'A bracelet laid flat, measured from end to end including the clasp',
    lineOne: '1 line',
    lineMany: '{n} lines',
    smallHelp: 'Shorter lines engrave bigger. Lines near the top and bottom of the plate have less room, so keep those short.',
    engraveTitle: 'Your engraving',
    engraveLabel: 'Type it exactly as you want it engraved on the back of the plate. Press Enter for a new line.',
    fontTitle: 'Font',
    extrasTitle: 'Extras',
    optional: 'Optional',
    quickAdd: 'Quick add',
    sizeLabel: 'Text size on plate',
    sizeGreat: 'Easy to read',
    sizeGood: 'Good',
    sizeSmall: 'Small, still clear',
    sizeTooSmall: 'Too small to engrave clearly',
    tooSmallHelp: 'Shorten your longest line, or use fewer lines, to make the text bigger.',
    moreLinesNote: 'More lines means smaller text. Up to {n} lines fit on this plate.',
    previewNote: 'Preview is a close guide. Final spacing may vary slightly.',
    twinNote: 'Shown in a close match on this device. Engraved in MV Boli.',
    example: 'Example',
    livePreview: 'Engraving preview',
    backOfPlate: 'Back of plate',
    zoomIn: 'Zoom in on the engraving',
    zoomOut: 'Show the whole plate',
    summaryTitle: 'Your bracelet',
    total: 'Total',
    addToCart: 'Add to cart',
    adding: 'Adding...',
    added: 'Added to your cart',
    soldOut: 'Sold out',
    unavailable: 'This colour and size is sold out. Choose another.',
    needColour: 'Choose a colour.',
    needSize: 'Choose your size.',
    needText: 'Add your engraving.',
    needConfirm: 'Tick the box to confirm your engraving.',
    maxLines: 'Maximum {n} lines.',
    maxChars: '{n} character limit reached.',
    emojiRemoved: 'Emojis can\u2019t be engraved, so they were removed.',
    charsCount: '{n} / {max} characters',
    linesCount: '{n} / {max} lines',
    freeShipYes: 'This order ships free within Australia.',
    freeShipNo: 'Add {amount} more for free shipping within Australia.',
    bracelet: 'Bracelet',
    forLabel: 'For',
    error: 'Something went wrong adding this to your cart. Please try again.'
  };

  /* ------------------------------------------------------------------ *
   * Small helpers
   * ------------------------------------------------------------------ */
  function h(tag, attrs, kids) { var n = document.createElement(tag); setAttrs(n, attrs); append(n, kids); return n; }
  function sv(tag, attrs, kids) { var n = document.createElementNS(SVGNS, tag); setAttrs(n, attrs); append(n, kids); return n; }
  function setAttrs(n, attrs) {
    if (!attrs) return;
    Object.keys(attrs).forEach(function (k) {
      var v = attrs[k];
      if (v === null || v === undefined || v === false) return;
      if (k === 'class') n.setAttribute('class', v);
      else if (k === 'text') n.textContent = v;
      else if (k === 'style' && typeof v === 'object') Object.keys(v).forEach(function (p) { n.style.setProperty(p, v[p]); });
      else if (k.indexOf('on') === 0 && typeof v === 'function') n.addEventListener(k.slice(2), v);
      else n.setAttribute(k, v === true ? '' : String(v));
    });
  }
  function append(n, kids) {
    (kids || []).forEach(function (c) {
      if (c === null || c === undefined || c === false) return;
      n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
  }
  function norm(s) { return String(s === undefined || s === null ? '' : s).trim().toLowerCase(); }
  function fmt(str, vars) { return String(str).replace(/\{(\w+)\}/g, function (m, k) { return vars[k] !== undefined ? vars[k] : m; }); }
  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
  function lines(str) { return String(str || '').split(/\r?\n/).map(function (s) { return s.trim(); }).filter(Boolean); }
  function chars(str) { return Array.from(String(str || '').replace(/\n/g, '')).length; }
  function decodeEntities(str) { var t = document.createElement('textarea'); t.innerHTML = str || ''; return t.value; }
  function hexToRgb(hex) {
    var m = String(hex || '').replace('#', '').trim();
    if (m.length === 3) m = m.split('').map(function (c) { return c + c; }).join('');
    var n = parseInt(m.slice(0, 6), 16);
    if (isNaN(n)) return { r: 128, g: 128, b: 128 };
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }
  function rgbToHex(c) { return '#' + [c.r, c.g, c.b].map(function (v) { return ('0' + Math.round(clamp(v, 0, 255)).toString(16)).slice(-2); }).join(''); }
  function mix(a, b, t) { var x = hexToRgb(a), y = hexToRgb(b); return rgbToHex({ r: x.r + (y.r - x.r) * t, g: x.g + (y.g - x.g) * t, b: x.b + (y.b - x.b) * t }); }
  function luminance(hex) {
    var c = hexToRgb(hex);
    var f = function (v) { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
    return 0.2126 * f(c.r) + 0.7152 * f(c.g) + 0.0722 * f(c.b);
  }
  function formatMoney(cents, format) {
    var tpl = decodeEntities(String(format || '${{amount}}').replace(/<[^>]*>/g, ''));
    var re = /\{\{\s*(\w+)\s*\}\}/;
    function delim(num, precision, thousands, decimal) {
      var n = (num / 100).toFixed(precision);
      var p = n.split('.');
      return p[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands) + (p[1] ? decimal + p[1] : '');
    }
    var m = tpl.match(re);
    var v;
    switch (m ? m[1] : 'amount') {
      case 'amount_no_decimals': v = delim(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': v = delim(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': v = delim(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': v = delim(cents, 2, '\'', '.'); break;
      default: v = delim(cents, 2, ',', '.');
    }
    return m ? tpl.replace(re, v) : '$' + v;
  }
  // "Name | light | dark | engraving colour"
  function parseColourStyles(text) {
    var out = {};
    lines(text).forEach(function (l) {
      var p = l.split('|').map(function (s) { return s.trim(); });
      if (p.length >= 4) out[norm(p[0])] = { name: p[0], light: p[1], dark: p[2], text: p[3] };
    });
    return out;
  }
  // "Label | face | Name sent to order"
  function parseFonts(text) {
    var out = [];
    lines(text).forEach(function (l) {
      var p = l.split('|').map(function (s) { return s.trim(); });
      if (!p[0]) return;
      out.push({ label: p[0], key: norm(p[1] || p[0]).replace(/\s+/g, ''), engrave: p[2] || p[0] });
    });
    return out;
  }
  // Sort sizes by the number in them (16 cm, 18 cm ... 26 cm)
  function sizeNum(s) { var m = String(s).match(/[\d.]+/); return m ? parseFloat(m[0]) : 0; }

  var PICTO;
  try { PICTO = new RegExp('[\\p{Extended_Pictographic}\\u{1F1E6}-\\u{1F1FF}\\u{FE0F}\\u{200D}\\u{20E3}]', 'gu'); }
  catch (e) { PICTO = /[\uD83C-\uDBFF][\uDC00-\uDFFF]|[\u2600-\u27bf]|\ufe0f|\u200d/g; }

  /* ------------------------------------------------------------------ *
   * Text measurement (canvas, same faces as the preview)
   * ------------------------------------------------------------------ */
  var Measure = {
    ctx: null,
    cache: {},
    reset: function () { this.cache = {}; },
    get: function (faceCss) {
      if (!this.ctx) this.ctx = document.createElement('canvas').getContext('2d');
      if (this.cache[faceCss]) return this.cache[faceCss];
      var ctx = this.ctx;
      ctx.font = '100px ' + faceCss + ', sans-serif';
      var mH = ctx.measureText('H');
      var mD = ctx.measureText('gjpqy');
      var cap = mH.actualBoundingBoxAscent ? mH.actualBoundingBoxAscent / 100 : 0.716;
      var desc = mD.actualBoundingBoxDescent ? mD.actualBoundingBoxDescent / 100 : 0.21;
      var info = { cap: cap, desc: desc, widths: {} };
      this.cache[faceCss] = info;
      return info;
    },
    width: function (faceCss, text) {
      var info = this.get(faceCss);
      if (info.widths[text] === undefined) {
        this.ctx.font = '100px ' + faceCss + ', sans-serif';
        info.widths[text] = this.ctx.measureText(text).width / 100;
      }
      return info.widths[text];
    }
  };

  /* ------------------------------------------------------------------ *
   * Builder
   * ------------------------------------------------------------------ */
  function Builder(root) {
    this.root = root;
    this.uid = 'apb' + Math.random().toString(36).slice(2, 8);
    this.cfg = JSON.parse(root.querySelector('script[data-apb-config]').textContent);
    this.t = Object.assign({}, T, this.cfg.text || {});
    this.money = this.cfg.moneyFormat || '${{amount}}';
    this.steps = [];
    this.previews = [];
    this.cartSubtotal = 0;
    this.init();
  }

  Builder.prototype.init = function () {
    var c = this.cfg, p = c.product, self = this;
    this.eng = Object.assign({
      maxChars: 120, maxLines: 7, plateW: 37, plateH: 15.5, edge: 0.6, lineW: 25,
      minCap: 1.0, goodCap: 1.5, maxFont: 4, lineHeight: 1.22, required: true,
      chips: '', placeholder: '', lineLabel: 'Line', fontLabel: 'Font'
    }, c.engraving || {});

    // Which option is the colour, which is the size
    var opts = (p.options || []).map(norm);
    var ci = opts.indexOf(norm(p.colourOption));
    if (ci < 0) ci = opts.findIndex(function (o) { return /colou?r|metal|finish|tone/.test(o); });
    var si = opts.indexOf(norm(p.sizeOption));
    if (si < 0) si = opts.findIndex(function (o, i) { return i !== ci && /size|length/.test(o); });
    if (ci < 0) ci = 0;
    if (si < 0 && opts.length > 1) si = ci === 0 ? 1 : 0;
    this.ci = ci; this.si = si;
    this.variants = p.variants || [];

    var colourValues = [], sizeValues = [];
    this.variants.forEach(function (v) {
      var cv = v.options[ci], sz = si >= 0 ? v.options[si] : null;
      if (cv !== undefined && colourValues.indexOf(cv) < 0) colourValues.push(cv);
      if (sz !== null && sz !== undefined && sizeValues.indexOf(sz) < 0) sizeValues.push(sz);
    });
    this.sizes = sizeValues.slice().sort(function (a, b) { return sizeNum(a) - sizeNum(b); });

    var styles = parseColourStyles(c.colourStyles);
    var order = Object.keys(styles);
    var assets = c.plateAssets || {};
    // Colour photos: the image assigned to that colour's variants if there is one; otherwise
    // the first photo whose alt text names this colour and no other one
    // (e.g. "Lustre Bloom in Rose Gold"). Either way the page still opens on the first image.
    var media = c.media || [];
    var esc = function (x) { return x.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); };
    var mentions = function (alt, name) {
      return new RegExp('(^|[^a-z])' + esc(norm(name)) + '([^a-z]|$)').test(norm(alt));
    };
    var byLength = colourValues.slice().sort(function (x, y) { return y.length - x.length; });
    var coloursIn = function (alt) {
      var text = norm(alt), found = [];
      byLength.forEach(function (col) {
        if (mentions(text, col)) { found.push(col); text = text.split(norm(col)).join(' '); }
      });
      return found;
    };
    var photoFor = function (name) {
      return media.find(function (m) { var cs = coloursIn(m.alt); return cs.length === 1 && cs[0] === name; }) || null;
    };
    this.colours = colourValues.slice().sort(function (a, b) {
      var ia = order.indexOf(norm(a)), ib = order.indexOf(norm(b));
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    }).map(function (v) {
      var st = styles[norm(v)] || FALLBACK_COLOURS[norm(v)] || { light: '#eceef0', dark: '#a6abb1', text: '#4a4e54' };
      return { name: v, light: st.light, dark: st.dark, text: st.text, plate: assets[v] || '', photo: (c.variantPhotos && c.variantPhotos[v]) || photoFor(v) };
    });

    this.fonts = parseFonts(c.fonts).map(function (f) {
      var face = FACES[f.key];
      return { label: f.label, key: f.key, engrave: f.engrave, css: face ? face.css : f.key, stack: face ? face.stack : f.key, face: face || {} };
    });
    if (!this.fonts.length) this.fonts = [{ label: 'Arial', key: 'arial', engrave: 'Arial', css: FACES.arial.css, stack: FACES.arial.stack, face: FACES.arial }];

    var ad = c.addons || {};
    this.addons = {
      packaging: ad.packaging && ad.packaging.id ? ad.packaging : null,
      card: ad.card && ad.card.id ? ad.card : null
    };

    var init = this.variants.find(function (v) { return String(v.id) === String(p.initialVariant); }) || this.variants.find(function (v) { return v.available; }) || this.variants[0];
    // Size starts unchosen unless the URL named a variant or there is only one size
    this.state = {
      // Nothing is chosen for the customer unless the link named a variant
      colour: (p.variantInUrl && init) ? init.options[ci] : (this.colours.length === 1 ? this.colours[0].name : null),
      size: (si >= 0 && (p.variantInUrl || this.sizes.length === 1) && init) ? init.options[si] : null,
      text: '',
      font: 0,
      packaging: false,
      card: false,
      confirmed: false,
      zoom: false
    };
    if (si < 0) this.state.size = '';

    this.render();
    this.loadFonts();
    this.fetchCart();
    this.update();
    if (p.variantInUrl) this.showColourPhoto();
    this.root.classList.add('is-ready');
  };

  /* ---------------- lookups ---------------- */
  Builder.prototype.variantFor = function (colour, size) {
    var ci = this.ci, si = this.si;
    return this.variants.find(function (v) { return v.options[ci] === colour && (si < 0 || v.options[si] === size); }) || null;
  };
  Builder.prototype.variant = function () { return this.variantFor(this.state.colour, this.state.size); };
  Builder.prototype.colourObj = function (name) {
    name = name || this.state.colour;
    return this.colours.find(function (f) { return f.name === name; }) || this.colours[0];
  };
  Builder.prototype.fontObj = function () { return this.fonts[this.state.font] || this.fonts[0]; };
  Builder.prototype.$ = function (sel) { return this.root.querySelector(sel); };
  Builder.prototype.$all = function (sel) { return Array.prototype.slice.call(this.root.querySelectorAll(sel)); };

  /* ---------------- engraving ---------------- */
  Builder.prototype.engravingLines = function () {
    return String(this.state.text || '').split('\n').map(function (l) { return l.trim(); }).filter(Boolean);
  };

  // Room for a line on the oval: the chord of the inner oval at the line's top and bottom,
  // capped by the gap between the two chain slots.
  Builder.prototype.lineRoom = function (yTop, yBot) {
    var e = this.eng;
    var a = e.plateW / 2 - e.edge, b = e.plateH / 2 - e.edge;
    var dy = Math.max(Math.abs(yTop), Math.abs(yBot));
    if (dy >= b) return 0;
    var chord = 2 * a * Math.sqrt(1 - (dy * dy) / (b * b));
    return Math.min(chord, e.lineW);
  };

  // Font size (mm) that fits every line inside the oval
  Builder.prototype.fit = function (lineArr, font) {
    var e = this.eng, css = font.css, m = Measure.get(css), n = lineArr.length;
    if (!n) return null;
    var widths = lineArr.map(function (l) { return Measure.width(css, l); });
    var innerH = e.plateH - 2 * e.edge;
    var self = this;
    // Every line fits at this font size? Smaller text also moves the outer lines inward,
    // where the oval is wider, so search for the largest size rather than scaling once.
    var fits = function (fs) {
      for (var i = 0; i < n; i++) {
        var yc = (i - (n - 1) / 2) * e.lineHeight * fs;            // line centre, from plate centre
        var room = self.lineRoom(yc - fs * m.cap / 2, yc + fs * (m.cap / 2 + m.desc * 0.5));
        if (widths[i] * fs > room) return false;
      }
      return true;
    };
    var fs = Math.min(e.maxFont, innerH / ((n - 1) * e.lineHeight + m.cap + m.desc * 0.75));
    if (!fits(fs)) {
      var lo = 0, hi = fs;
      for (var iter = 0; iter < 22; iter++) {
        var mid = (lo + hi) / 2;
        if (fits(mid)) lo = mid; else hi = mid;
      }
      fs = lo;
    }
    var cap = fs * m.cap;
    var c1 = Math.round(cap * 10) / 10;
    var level = c1 >= e.goodCap ? 'great' : c1 >= (e.minCap + e.goodCap) / 2 ? 'good' : c1 >= e.minCap ? 'small' : 'tiny';
    return { fs: fs, cap: cap, capRatio: m.cap, lh: e.lineHeight, level: level, n: n };
  };

  Builder.prototype.sanitize = function (value) {
    var e = this.eng, flags = {};
    var v = String(value || '').replace(/\r\n?/g, '\n').replace(/\t/g, ' ');
    var noEmoji = v.replace(PICTO, '');
    if (noEmoji !== v) flags.emoji = true;
    v = noEmoji;
    var ls = v.split('\n');
    if (ls.length > e.maxLines) {
      var keep = ls.slice(0, e.maxLines - 1);
      keep.push(ls.slice(e.maxLines - 1).join(' '));
      ls = keep;
      flags.lines = true;
    }
    v = ls.join('\n');
    var count = 0, out = '';
    Array.from(v).forEach(function (ch) {
      if (ch === '\n') { out += ch; return; }
      if (count >= e.maxChars) { flags.chars = true; return; }
      out += ch; count++;
    });
    return { value: out, flags: flags };
  };

  /* ------------------------------------------------------------------ *
   * Rendering
   * ------------------------------------------------------------------ */
  Builder.prototype.render = function () {
    var self = this, t = this.t;

    this.shot = this.$('[data-apb-shot]');
    this.shotPhoto = this.$('[data-apb-photo]');
    this.stage = this.$('[data-apb-stage]');
    var stagePreview = this.$('[data-apb-preview]');
    if (stagePreview) {
      this.previews.push(new Preview(this, stagePreview, { suffix: 'main' }));
      this.zoomBtn = h('button', { type: 'button', class: 'apb-stage__zoom', 'aria-pressed': 'false', 'aria-label': t.zoomIn, title: t.zoomIn, onclick: function () { self.toggleZoom(); } }, [this.icon('zoom')]);
      this.liveTag = h('span', { class: 'apb-stage__tag' }, [h('i', { 'aria-hidden': 'true' }), h('span', { text: t.livePreview + ' \u00b7 ' + t.backOfPlate })]);
      this.exampleTag = h('span', { class: 'apb-stage__example', text: t.example + ' text' });
      append(this.stage, [this.liveTag, this.zoomBtn, this.exampleTag]);
    }
    this.$all('[data-apb-thumb]').forEach(function (btn) {
      btn.addEventListener('click', function () { self.showPhoto(btn.getAttribute('data-src'), btn.getAttribute('data-alt')); });
    });
    this.buildGallery();

    var mount = this.$('[data-apb-steps]');
    if (!mount) return;
    mount.innerHTML = '';
    this._stepsMount = mount;
    this.buildColour();
    if (this.si >= 0) this.buildSize();
    this.buildEngraving();
    if (this.fonts.length > 1) this.buildFont();
    if (this.addons.packaging || this.addons.card) this.buildExtras();
    this.buildSummary(mount);
    this.buildBar();
    var n = 0;
    this.steps.forEach(function (s) { n++; s.index = n; s.num.textContent = n; });
  };

  Builder.prototype.icon = function (name) {
    var paths = {
      zoom: 'M10.5 4a6.5 6.5 0 1 0 4.06 11.58l4.43 4.43 1.41-1.41-4.43-4.43A6.5 6.5 0 0 0 10.5 4Zm0 2a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Zm-1 1.75v2h-2v1.5h2v2h1.5v-2h2v-1.5h-2v-2h-1.5Z',
      unzoom: 'M10.5 4a6.5 6.5 0 1 0 4.06 11.58l4.43 4.43 1.41-1.41-4.43-4.43A6.5 6.5 0 0 0 10.5 4Zm0 2a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Zm-3 3.75v1.5h6v-1.5h-6Z',
      check: 'M9.5 16.2 5.3 12l-1.4 1.4 5.6 5.6L20.1 8.4 18.7 7l-9.2 9.2Z',
      left: 'M15.4 5.4 14 4l-8 8 8 8 1.4-1.4L8.8 12l6.6-6.6Z',
      expand: 'M4 4h6v2H6v4H4V4Zm10 0h6v6h-2V6h-4V4ZM4 14h2v4h4v2H4v-6Zm14 4v-4h2v6h-6v-2h4Z',
      right: 'M8.6 18.6 10 20l8-8-8-8-1.4 1.4 6.6 6.6-6.6 6.6Z'
    };
    return sv('svg', { viewBox: '0 0 24 24', width: 20, height: 20, 'aria-hidden': 'true', focusable: 'false' }, [sv('path', { d: paths[name], fill: 'currentColor' })]);
  };

  Builder.prototype.step = function (key, title, opts) {
    opts = opts || {};
    var num = h('span', { class: 'apb-step__num', 'aria-hidden': 'true' });
    var value = h('span', { class: 'apb-step__value' });
    var head = h('div', { class: 'apb-step__head' }, [num, h('h3', { class: 'apb-step__title', text: title }), value]);
    var body = h('div', { class: 'apb-step__body' });
    var msg = h('p', { class: 'apb-step__msg', role: 'alert' });
    msg.hidden = true;
    var sec = h('section', { class: 'apb-step', 'data-step': key, 'aria-label': title }, [head, body, msg]);
    var rec = { key: key, el: sec, num: num, value: value, body: body, msg: msg, optional: !!opts.optional };
    this.steps.push(rec);
    this._stepsMount.appendChild(sec);
    return rec;
  };

  /* ---- 1. Colour ---- */
  Builder.prototype.buildColour = function () {
    var self = this, t = this.t;
    var st = this.step('colour', t.colourTitle);
    var group = h('div', { class: 'apb-colours', role: 'radiogroup', 'aria-label': t.colourTitle });
    this.colourInputs = {};
    this.colours.forEach(function (f, i) {
      var id = self.uid + '-col-' + i;
      var input = h('input', { type: 'radio', class: 'apb-vh', id: id, name: self.uid + '-colour', value: f.name });
      input.addEventListener('change', function () { self.set({ colour: f.name }); });
      var media = h('span', { class: 'apb-colour__media', style: { '--l': f.light, '--d': f.dark } });
      if (f.photo && f.photo.thumb) {
        media.appendChild(h('img', { class: 'apb-colour__photo', src: f.photo.thumb, alt: '', width: 120, height: 120, loading: 'lazy' }));
      } else {
        media.classList.add('is-drawn');
      }
      var lab = h('label', { class: 'apb-colour', for: id }, [media, h('span', { class: 'apb-colour__name', text: f.name })]);
      self.colourInputs[f.name] = { input: input, label: lab };
      group.appendChild(input);
      group.appendChild(lab);
    });
    st.body.appendChild(group);
  };

  /* ---- 2. Size ---- */
  Builder.prototype.buildSize = function () {
    var self = this, t = this.t, c = this.cfg;
    var st = this.step('size', t.sizeTitle);
    var group = h('div', { class: 'apb-sizeopts', role: 'radiogroup', 'aria-label': t.sizeTitle });
    this.sizeInputs = {};
    this.sizes.forEach(function (sz, i) {
      var id = self.uid + '-size-' + i;
      var input = h('input', { type: 'radio', class: 'apb-vh', id: id, name: self.uid + '-size', value: sz });
      input.addEventListener('change', function () { self.set({ size: sz }); });
      var lab = h('label', { class: 'apb-sizeopt', for: id, text: sz });
      self.sizeInputs[sz] = { input: input, label: lab };
      group.appendChild(input);
      group.appendChild(lab);
    });
    st.body.appendChild(group);
    var note = c.sizeNote || '';
    var guide = c.guide && c.guide.enabled;
    if (note || guide) {
      var p = h('p', { class: 'apb-help apb-size-note' + (c.chainSwap ? ' is-swap' : '') });
      if (note) p.appendChild(document.createTextNode(note + ' '));
      if (guide) {
        this.guideBtn = h('button', { type: 'button', class: 'apb-linkbtn', 'aria-expanded': 'false', text: t.sizeGuide, onclick: function () { self.toggleGuide(); } });
        p.appendChild(this.guideBtn);
      }
      st.body.appendChild(p);
      if (guide) { this.guideMount = h('div', { class: 'apb-guide-mount' }); st.body.appendChild(this.guideMount); }
    }
  };

  /* ---- Size guide popup: how to measure + a size suggestion from this product's sizes ---- */
  Builder.prototype.buildGuide = function () {
    var self = this, t = this.t, g = this.cfg.guide || {};
    var titleId = this.uid + '-guide-title';
    var close = h('button', { type: 'button', class: 'apb-modal__close', 'aria-label': t.guideClose, onclick: function () { self.closeGuide(); try { self.guideBtn.focus(); } catch (e) { /* ignore */ } } }, [
      sv('svg', { viewBox: '0 0 24 24', width: 20, height: 20, 'aria-hidden': 'true' }, [sv('path', { d: 'M6.4 5 5 6.4 10.6 12 5 17.6 6.4 19l5.6-5.6 5.6 5.6 1.4-1.4-5.6-5.6L19 6.4 17.6 5 12 10.6 6.4 5Z', fill: 'currentColor' })])
    ]);

    // Best way first: measure a bracelet or watch that already fits
    var best = h('div', { class: 'apb-guide__best' }, [
      h('span', { class: 'apb-guide__badge', text: t.guideBestBadge }),
      h('h3', { class: 'apb-guide__h', text: t.guideBestTitle }),
      this.guideArt(),
      h('p', { class: 'apb-guide__p', text: g.best || t.guideBestText })
    ]);
    var steps = h('ol', { class: 'apb-guide__steps' });
    lines(g.steps).forEach(function (s) { steps.appendChild(h('li', { text: s })); });
    var wrist = h('div', { class: 'apb-guide__wrist' }, [h('h3', { class: 'apb-guide__h', text: t.guideWristTitle }), steps]);

    // Calculator: a bracelet length, or a wrist size
    var mode = 'bracelet';
    var inId = this.uid + '-measure';
    var label = h('label', { class: 'apb-guide__label', for: inId, text: t.guideLenLabel });
    var input = h('input', { type: 'number', id: inId, class: 'apb-guide__input', inputmode: 'decimal', min: 10, max: 32, step: 0.5, placeholder: '18' });
    var result = h('div', { class: 'apb-guide__result', 'aria-live': 'polite' });
    var modes = h('div', { class: 'apb-guide__modes', role: 'radiogroup', 'aria-label': t.guideCalcTitle });
    var modeBtns = {};
    [['bracelet', t.guideModeBracelet], ['wrist', t.guideModeWrist]].forEach(function (m) {
      var b = h('button', { type: 'button', class: 'apb-guide__mode', role: 'radio', 'aria-checked': m[0] === mode ? 'true' : 'false', text: m[1], onclick: function () {
        mode = m[0];
        Object.keys(modeBtns).forEach(function (k) { modeBtns[k].setAttribute('aria-checked', k === mode ? 'true' : 'false'); });
        label.textContent = mode === 'bracelet' ? t.guideLenLabel : t.guideWristLabel;
        input.placeholder = mode === 'bracelet' ? '18' : '16.5';
        suggest();
        try { input.focus(); } catch (e) { /* ignore */ }
      } });
      modeBtns[m[0]] = b;
      modes.appendChild(b);
    });
    var suggest = function () {
      result.innerHTML = '';
      var w = parseFloat(String(input.value).replace(',', '.'));
      if (!w || w < 10 || w > 32) return;
      var nums = self.sizes.map(function (s) { return { s: s, n: sizeNum(s) }; });
      if (!nums.length) return;
      var pick, msg;
      if (mode === 'bracelet') {
        // the size closest to a bracelet that already fits; on a tie, the larger one
        pick = nums.reduce(function (best, x) {
          var d = Math.abs(x.n - w), bd = Math.abs(best.n - w);
          return d < bd || (d === bd && x.n > best.n) ? x : best;
        }, nums[0]);
        msg = Math.abs(pick.n - w) > 1.5
          ? (pick.n < w ? fmt(t.guideTooBig, { size: pick.s }) : fmt(t.guideTooSmall, { size: pick.s }))
          : fmt(t.guideSuggestLen, { size: pick.s });
      } else {
        // wrist plus 1 to 2 cm
        pick = nums.find(function (x) { return x.n >= w + 1 - 0.25; });
        if (!pick) { pick = nums[nums.length - 1]; msg = fmt(t.guideTooBig, { size: pick.s }); }
        else if (pick === nums[0] && pick.n > w + 3) msg = fmt(t.guideTooSmall, { size: pick.s });
        else msg = fmt(t.guideSuggestWrist, { size: pick.s });
      }
      var anyAvailable = function (sz) {
        if (self.state.colour) { var v = self.variantFor(self.state.colour, sz); return !!(v && v.available); }
        return self.variants.some(function (v) { return v.options[self.si] === sz && v.available; });
      };
      var btn = h('button', { type: 'button', class: 'apb-guide__pick', text: fmt(t.guideChoose, { size: pick.s }), onclick: function () {
        self.set({ size: pick.s });
        self.closeGuide();
        var sizes = self.$('.apb-step[data-step="size"]');
        if (sizes) { var y = sizes.getBoundingClientRect().top + window.pageYOffset - (self.cfg.scrollOffset || 110); if (y < window.pageYOffset) window.scrollTo({ top: y, behavior: 'smooth' }); }
      } });
      if (!anyAvailable(pick.s)) { btn.disabled = true; btn.textContent = t.soldOut; }
      append(result, [h('p', { class: 'apb-guide__msg', text: msg }), btn]);
    };
    input.addEventListener('input', suggest);
    var calc = h('div', { class: 'apb-guide__calc' }, [
      h('h3', { class: 'apb-guide__h', text: t.guideCalcTitle }),
      modes,
      label,
      h('div', { class: 'apb-guide__row' }, [input, h('span', { class: 'apb-guide__unit', text: 'cm' })]),
      result
    ]);

    var kids = [
      h('div', { class: 'apb-modal__head' }, [h('h2', { class: 'apb-modal__title', id: titleId, text: g.title || t.guideTitle }), close]),
      best,
      wrist,
      g.calc ? calc : null,
      h('p', { class: 'apb-guide__sizes', text: fmt(t.guideSizes, { list: this.sizes.join(', ') }) + ' ' + t.guideBetween })
    ];
    if (this.cfg.chainSwap && this.cfg.sizeNote) kids.push(h('p', { class: 'apb-guide__swap', text: this.cfg.sizeNote }));
    // Opens in place, right under the sizes: no overlay, and the customer never loses their spot
    this.guide = h('div', { class: 'apb-guide-panel', id: this.uid + '-guide', role: 'region', 'aria-labelledby': titleId }, [h('div', { class: 'apb-modal__inner' }, kids)]);
    this.guide.hidden = true;
    this.guide.addEventListener('keydown', function (ev) { if (ev.key === 'Escape') { self.closeGuide(); try { self.guideBtn.focus(); } catch (e) { /* ignore */ } } });
    this.guideInput = input;
    (this.guideMount || this.root).appendChild(this.guide);
    if (this.guideBtn) this.guideBtn.setAttribute('aria-controls', this.guide.id);
  };

  // A bracelet laid flat with a measuring arrow from clasp to clasp
  Builder.prototype.guideArt = function () {
    var W = 520, Ht = 116, y = 36;
    var g = sv('svg', { class: 'apb-guide__art', viewBox: '0 0 ' + W + ' ' + Ht, role: 'img', 'aria-label': this.t.guideArtLabel });
    var metal = '#b9a27a', dark = '#8c7650';
    // chain links
    for (var x = 58; x <= W - 58; x += 18) {
      if (x > 196 && x < 324) continue;
      g.appendChild(sv('ellipse', { cx: x, cy: y, rx: 9, ry: 6, fill: 'none', stroke: metal, 'stroke-width': 3 }));
    }
    // plate in the middle
    g.appendChild(sv('ellipse', { cx: W / 2, cy: y, rx: 66, ry: 26, fill: '#efe6d6', stroke: dark, 'stroke-width': 2 }));
    g.appendChild(sv('ellipse', { cx: W / 2 - 50, cy: y, rx: 7, ry: 9, fill: '#fff', stroke: dark, 'stroke-width': 1.5 }));
    g.appendChild(sv('ellipse', { cx: W / 2 + 50, cy: y, rx: 7, ry: 9, fill: '#fff', stroke: dark, 'stroke-width': 1.5 }));
    // clasp + end ring
    g.appendChild(sv('path', { d: 'M28 ' + (y - 9) + ' q-12 9 0 18 l14 0 q6 -9 0 -18 Z', fill: 'none', stroke: dark, 'stroke-width': 3 }));
    g.appendChild(sv('circle', { cx: W - 36, cy: y, r: 8, fill: 'none', stroke: dark, 'stroke-width': 3 }));
    // arrow end to end
    var ay = 76;
    g.appendChild(sv('line', { x1: 18, y1: ay, x2: W - 26, y2: ay, stroke: '#03857c', 'stroke-width': 2.5 }));
    g.appendChild(sv('path', { d: 'M18 ' + ay + ' l10 -6 l0 12 Z', fill: '#03857c' }));
    g.appendChild(sv('path', { d: 'M' + (W - 26) + ' ' + ay + ' l-10 -6 l0 12 Z', fill: '#03857c' }));
    g.appendChild(sv('line', { x1: 18, y1: ay - 14, x2: 18, y2: ay + 8, stroke: '#03857c', 'stroke-width': 1.5 }));
    g.appendChild(sv('line', { x1: W - 26, y1: ay - 14, x2: W - 26, y2: ay + 8, stroke: '#03857c', 'stroke-width': 1.5 }));
    g.appendChild(sv('text', { x: W / 2, y: ay + 30, 'text-anchor': 'middle', 'font-size': 17, 'font-weight': 700, fill: '#03857c', 'font-family': 'inherit', text: this.t.guideArtText }));
    return g;
  };

  Builder.prototype.toggleGuide = function () {
    if (this.guide && !this.guide.hidden) this.closeGuide(); else this.openGuide();
  };

  Builder.prototype.openGuide = function () {
    if (!this.guide) this.buildGuide();
    this.guide.hidden = false;
    if (this.guideBtn) { this.guideBtn.setAttribute('aria-expanded', 'true'); this.guideBtn.textContent = this.t.sizeGuideHide; }
    var panel = this.guide, off = this.cfg.scrollOffset || 110;
    // bring the panel's top into view if it opened below the fold
    var top = panel.getBoundingClientRect().top;
    if (top > (window.innerHeight || 800) * 0.7) window.scrollTo({ top: top + window.pageYOffset - off - 60, behavior: 'smooth' });
  };

  Builder.prototype.closeGuide = function () {
    if (!this.guide || this.guide.hidden) return;
    this.guide.hidden = true;
    if (this.guideBtn) { this.guideBtn.setAttribute('aria-expanded', 'false'); this.guideBtn.textContent = this.t.sizeGuide; }
  };

  /* ---- 3. Engraving ---- */
  Builder.prototype.buildEngraving = function () {
    var self = this, t = this.t, e = this.eng;
    var st = this.step('engraving', t.engraveTitle);
    var id = this.uid + '-text';
    var ta = h('textarea', { id: id, class: 'apb-textarea', rows: 5, autocomplete: 'off', autocapitalize: 'sentences', spellcheck: 'true', 'aria-describedby': id + '-meta' });
    ta.placeholder = e.placeholder || '';
    this.textarea = ta;
    var count = h('span', { class: 'apb-count' });
    var lineCount = h('span', { class: 'apb-count' });
    var meta = h('div', { class: 'apb-engrave__meta', id: id + '-meta' }, [count, lineCount]);
    this.countEl = count; this.lineEl = lineCount;

    this.sizeBar = h('i', { class: 'apb-size__fill' });
    this.sizeVal = h('span', { class: 'apb-size__value' });
    this.sizeHint = h('p', { class: 'apb-size__hint' });
    this.sizeWrap = h('div', { class: 'apb-size', 'aria-live': 'polite' }, [
      h('span', { class: 'apb-size__label', text: t.sizeLabel }),
      h('span', { class: 'apb-size__track' }, [this.sizeBar]),
      this.sizeVal,
      this.sizeHint
    ]);

    var chipList = lines(e.chips);
    var chips = null;
    if (chipList.length) {
      chips = h('div', { class: 'apb-chips' }, [h('span', { class: 'apb-chips__label', text: t.quickAdd })]);
      chipList.forEach(function (c) { chips.appendChild(h('button', { type: 'button', class: 'apb-chip', text: c, onclick: function () { self.insertPhrase(c); } })); });
    }

    var inline = h('div', { class: 'apb-inline-preview', 'aria-hidden': 'true' });
    this.flash = h('p', { class: 'apb-flash', 'aria-live': 'polite' });
    this.flash.hidden = true;

    st.body.appendChild(h('label', { class: 'apb-label', for: id, text: t.engraveLabel }));
    st.body.appendChild(ta);
    st.body.appendChild(meta);
    st.body.appendChild(this.flash);
    st.body.appendChild(inline);
    st.body.appendChild(this.sizeWrap);
    if (chips) st.body.appendChild(chips);
    st.body.appendChild(h('p', { class: 'apb-help apb-preview-note', text: fmt(t.moreLinesNote, { n: e.maxLines }) + ' ' + t.previewNote }));
    this.previews.push(new Preview(this, inline, { suffix: 'inl', fixedZoom: true }));

    ta.addEventListener('keydown', function (ev) {
      if (ev.key === 'Enter' && !ev.isComposing) {
        var sel = ta.value.slice(ta.selectionStart, ta.selectionEnd);
        if (ta.value.split('\n').length >= e.maxLines && sel.indexOf('\n') < 0) {
          ev.preventDefault();
          self.showFlash(fmt(t.maxLines, { n: e.maxLines }));
        }
      }
    });
    ta.addEventListener('input', function () { self.onText(); });
  };

  Builder.prototype.onText = function () {
    var ta = this.textarea, t = this.t;
    var caret = ta.selectionStart;
    var res = this.sanitize(ta.value);
    if (res.value !== ta.value) {
      var before = this.sanitize(ta.value.slice(0, caret)).value.length;
      ta.value = res.value;
      try { ta.setSelectionRange(before, before); } catch (e) { /* ignore */ }
      if (res.flags.emoji) this.showFlash(t.emojiRemoved);
      else if (res.flags.chars) this.showFlash(fmt(t.maxChars, { n: this.eng.maxChars }));
      else if (res.flags.lines) this.showFlash(fmt(t.maxLines, { n: this.eng.maxLines }));
    }
    this.set({ text: ta.value });
  };

  Builder.prototype.insertPhrase = function (phrase) {
    var ta = this.textarea, add = phrase;
    if (/[:]$|\bto$/i.test(add)) add += ' ';
    var v = ta.value, start = ta.selectionStart, end = ta.selectionEnd;
    if (document.activeElement !== ta) { start = end = v.length; }
    var lineStart = v.lastIndexOf('\n', start - 1) + 1;
    var before = v.slice(lineStart, start);
    if (before.trim().length) add = (v.split('\n').length < this.eng.maxLines ? '\n' : ' ') + add;
    ta.value = v.slice(0, start) + add + v.slice(end);
    var pos = start + add.length;
    ta.focus();
    try { ta.setSelectionRange(pos, pos); } catch (e) { /* ignore */ }
    this.onText();
  };

  Builder.prototype.showFlash = function (msg) {
    var f = this.flash;
    f.textContent = msg;
    f.hidden = false;
    clearTimeout(this._flashT);
    this._flashT = setTimeout(function () { f.hidden = true; }, 3200);
  };

  /* ---- 4. Font ---- */
  Builder.prototype.buildFont = function () {
    var self = this, t = this.t;
    var st = this.step('font', t.fontTitle);
    var group = h('div', { class: 'apb-fonts', role: 'radiogroup', 'aria-label': t.fontTitle });
    this.fontInputs = [];
    this.fonts.forEach(function (f, i) {
      var id = self.uid + '-font-' + i;
      var input = h('input', { type: 'radio', class: 'apb-vh', id: id, name: self.uid + '-font', value: i });
      input.addEventListener('change', function () { self.set({ font: i }); });
      var sample = h('span', { class: 'apb-font__sample', style: { 'font-family': f.stack } });
      var lab = h('label', { class: 'apb-font', for: id }, [sample, h('span', { class: 'apb-font__name', text: f.label })]);
      self.fontInputs.push({ input: input, label: lab, sample: sample });
      group.appendChild(input);
      group.appendChild(lab);
    });
    this.fontNote = h('p', { class: 'apb-help apb-help--note' });
    this.fontNote.hidden = true;
    st.body.appendChild(group);
    st.body.appendChild(this.fontNote);
  };

  /* ---- 5. Extras ---- */
  Builder.prototype.buildExtras = function () {
    var self = this, t = this.t, a = this.addons;
    var st = this.step('extras', t.extrasTitle, { optional: true });
    var toggle = function (key, addon) {
      var id = self.uid + '-' + key;
      var input = h('input', { type: 'checkbox', class: 'apb-vh', id: id });
      input.addEventListener('change', function () { var o = {}; o[key] = input.checked; self.set(o); });
      var lab = h('label', { class: 'apb-extra apb-extra--toggle', for: id }, [
        addon.image ? h('span', { class: 'apb-extra__img' }, [h('img', { src: addon.image, alt: '', loading: 'lazy', width: 64, height: 64 })]) : null,
        h('span', { class: 'apb-extra__body' }, [
          h('span', { class: 'apb-extra__head' }, [h('span', { class: 'apb-extra__title', text: addon.title }), h('span', { class: 'apb-extra__price', text: '+' + formatMoney(addon.price, self.money) })]),
          addon.text ? h('span', { class: 'apb-extra__text', text: addon.text }) : null
        ]),
        h('span', { class: 'apb-switch', 'aria-hidden': 'true' }, [h('i')])
      ]);
      if (!addon.available) { input.disabled = true; lab.classList.add('is-unavailable'); }
      st.body.appendChild(input);
      st.body.appendChild(lab);
      return input;
    };
    if (a.packaging) this.packInput = toggle('packaging', a.packaging);
    if (a.card) this.cardInput = toggle('card', a.card);
  };

  /* ---- Summary + add to cart ---- */
  Builder.prototype.buildSummary = function (mount) {
    var self = this, t = this.t, c = this.cfg;
    this.sumList = h('ul', { class: 'apb-summary__lines' });
    this.sumTotal = h('span', { class: 'apb-summary__amount' });
    this.shipEl = h('p', { class: 'apb-ship' });
    this.shipEl.hidden = true;
    this.atc = h('button', { type: 'button', class: 'apb-atc', onclick: function () { self.addToCart(); } }, [h('span', { class: 'apb-atc__label', text: t.addToCart }), h('span', { class: 'apb-atc__price' })]);
    this.atcMsg = h('p', { class: 'apb-atc-msg', role: 'alert' });
    this.atcMsg.hidden = true;
    var kids = [h('h3', { class: 'apb-summary__title', text: t.summaryTitle }), this.sumList, h('div', { class: 'apb-summary__total' }, [h('span', { text: t.total }), this.sumTotal]), this.shipEl];
    if (c.confirm && c.confirm.enabled) {
      var cid = this.uid + '-confirm';
      this.confirmInput = h('input', { type: 'checkbox', id: cid, class: 'apb-check__input' });
      this.confirmInput.addEventListener('change', function () { self.set({ confirmed: self.confirmInput.checked }); });
      this.confirmWrap = h('label', { class: 'apb-check', for: cid }, [this.confirmInput, h('span', { class: 'apb-check__box', 'aria-hidden': 'true' }, [this.icon('check')]), h('span', { class: 'apb-check__text', text: c.confirm.text })]);
      kids.push(this.confirmWrap);
    }
    kids.push(this.atc, this.atcMsg);
    this.summary = h('div', { class: 'apb-summary' }, kids);
    mount.appendChild(this.summary);
  };

  Builder.prototype.buildBar = function () {
    var self = this, t = this.t;
    this.barTotal = h('span', { class: 'apb-bar__total' });
    this.bar = h('div', { class: 'apb-bar', 'aria-hidden': 'true' }, [
      h('div', { class: 'apb-bar__info' }, [h('span', { class: 'apb-bar__label', text: t.total }), this.barTotal]),
      h('button', { type: 'button', class: 'apb-bar__btn', tabindex: '-1', text: t.addToCart, onclick: function () { self.addToCart(); } })
    ]);
    this.root.appendChild(this.bar);
    var steps = this.$('[data-apb-steps]');
    var ticking = false;
    var sync = function () {
      ticking = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var top = steps.getBoundingClientRect().top;
      var sum = self.atc.getBoundingClientRect();
      var barH = self.bar.offsetHeight || 70;
      var atcVisible = sum.bottom > 0 && sum.top < vh - barH;
      self.bar.classList.toggle('is-shown', top < vh * 0.35 && !atcVisible && sum.top > 0);
    };
    var onScroll = function () { if (!ticking) { ticking = true; requestAnimationFrame(sync); } };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    sync();
  };

  /* ------------------------------------------------------------------ *
   * State + updates
   * ------------------------------------------------------------------ */
  Builder.prototype.set = function (patch) {
    var s = this.state;
    var colourChanged = patch.colour !== undefined && patch.colour !== s.colour;
    Object.keys(patch).forEach(function (k) { s[k] = patch[k]; });
    if (colourChanged) this.showColourPhoto();
    if (patch.colour !== undefined || patch.size !== undefined) this.syncUrl();
    if (patch.font !== undefined) this.loadFont(this.fontObj());
    this.clearErrors(Object.keys(patch));
    this.update();
  };

  Builder.prototype.update = function () {
    var self = this, s = this.state, t = this.t;
    var v = this.variant();
    var font = this.fontObj();
    var engr = this.engravingLines();
    var fit = this.fit(engr, font);
    this.lastFit = fit;

    this.previews.forEach(function (p) { p.update(); });

    Object.keys(this.colourInputs).forEach(function (name) {
      var o = self.colourInputs[name];
      o.input.checked = name === s.colour;
      var any = self.variants.some(function (x) { return x.options[self.ci] === name && x.available; });
      o.label.classList.toggle('is-unavailable', !any);
    });
    if (this.sizeInputs) {
      Object.keys(this.sizeInputs).forEach(function (sz) {
        var o = self.sizeInputs[sz];
        o.input.checked = sz === s.size;
        var ok = s.colour
          ? (function () { var vv = self.variantFor(s.colour, sz); return !!(vv && vv.available); })()
          : self.variants.some(function (x) { return x.options[self.si] === sz && x.available; });
        o.label.classList.toggle('is-unavailable', !ok);
      });
    }

    if (this.fontInputs) {
      var sampleText = engr[0] || lines(this.eng.placeholder)[0] || 'Aa Bb 123';
      if (sampleText.length > 18) sampleText = sampleText.slice(0, 17) + '\u2026';
      this.fontInputs.forEach(function (o, i) {
        var f = self.fonts[i];
        o.input.checked = i === s.font;
        o.sample.textContent = sampleText;
        o.sample.classList.toggle('is-twin', !!(f.face.twinStroke && !f.face._localOk));
      });
      var showTwin = !!(font.face.twinNote && !font.face._localOk);
      this.fontNote.hidden = !showTwin;
      if (showTwin) this.fontNote.textContent = t.twinNote;
    }
    if (this.packInput) this.packInput.checked = !!s.packaging;
    if (this.cardInput) this.cardInput.checked = !!s.card;

    var e = this.eng;
    this.countEl.textContent = fmt(t.charsCount, { n: chars(s.text), max: e.maxChars });
    this.lineEl.textContent = fmt(t.linesCount, { n: engr.length, max: e.maxLines });
    this.countEl.classList.toggle('is-max', chars(s.text) >= e.maxChars);
    if (fit) {
      this.sizeWrap.hidden = false;
      this.sizeBar.style.width = clamp((fit.cap - 0.6) / (2.6 - 0.6), 0.04, 1) * 100 + '%';
      this.sizeWrap.setAttribute('data-level', fit.level);
      var label = { great: t.sizeGreat, good: t.sizeGood, small: t.sizeSmall, tiny: t.sizeTooSmall }[fit.level];
      this.sizeVal.textContent = fit.cap.toFixed(1) + ' mm \u00b7 ' + label;
      this.sizeHint.textContent = fit.level === 'tiny' ? t.tooSmallHelp : fit.level === 'small' ? t.smallHelp : '';
      this.sizeHint.hidden = !this.sizeHint.textContent;
    } else {
      this.sizeWrap.hidden = true;
    }

    this.stepState();

    var total = this.total();
    var totalStr = formatMoney(total, this.money);
    this.$all('[data-apb-price]').forEach(function (n) { n.textContent = totalStr; });
    var cmp = this.$('[data-apb-compare]');
    if (cmp) {
      var hasCmp = v && v.compare && v.compare > v.price;
      cmp.hidden = !hasCmp;
      if (hasCmp) cmp.textContent = formatMoney(total + (v.compare - v.price), this.money);
    }
    this.sumTotal.textContent = totalStr;
    this.barTotal.textContent = totalStr;
    this.atc.querySelector('.apb-atc__price').textContent = totalStr;
    var soldOut = !!(s.size !== null && (!v || !v.available));
    this.atc.classList.toggle('is-soldout', soldOut);
    this.atc.querySelector('.apb-atc__label').textContent = soldOut ? t.soldOut : t.addToCart;
    this.renderSummary();
    this.renderShipping(total);
    this.syncAfterpay(total);
    if (this.exampleTag) this.exampleTag.hidden = engr.length > 0;
  };

  Builder.prototype.basePrice = function () {
    var v = this.variant();
    if (v) return v.price;
    var c = this.state.colour, ci = this.ci;
    var any = this.variants.find(function (x) { return x.options[ci] === c; }) || this.variants[0];
    return any ? any.price : 0;
  };

  Builder.prototype.total = function () {
    var s = this.state, a = this.addons;
    var total = this.basePrice();
    if (s.packaging && a.packaging) total += a.packaging.price;
    if (s.card && a.card) total += a.card.price;
    return total;
  };

  Builder.prototype.renderSummary = function () {
    var s = this.state, a = this.addons, t = this.t, self = this;
    var ul = this.sumList;
    ul.innerHTML = '';
    var row = function (label, sub, cents) {
      ul.appendChild(h('li', {}, [
        h('span', { class: 'apb-summary__item' }, [label, sub ? h('small', { text: sub }) : null]),
        h('span', { class: 'apb-summary__price', text: formatMoney(cents, self.money) })
      ]));
    };
    var engr = this.engravingLines();
    var name = [s.colour, s.size].filter(Boolean).join(' / ');
    row(t.bracelet + (name ? ' \u00b7 ' + name : ''), engr.length ? '\u201c' + engr.join(' / ') + '\u201d \u00b7 ' + this.fontObj().label : '', this.basePrice());
    if (s.packaging && a.packaging) row(a.packaging.title, '', a.packaging.price);
    if (s.card && a.card) row(a.card.title, '', a.card.price);
  };

  Builder.prototype.renderShipping = function (total) {
    var fs = this.cfg.freeShipping;
    if (!fs || !fs.enabled || !fs.threshold) { this.shipEl.hidden = true; return; }
    var have = total + (this.cartSubtotal || 0);
    this.shipEl.hidden = false;
    if (have >= fs.threshold) { this.shipEl.className = 'apb-ship is-done'; this.shipEl.textContent = this.t.freeShipYes; }
    else { this.shipEl.className = 'apb-ship'; this.shipEl.textContent = fmt(this.t.freeShipNo, { amount: formatMoney(fs.threshold - have, this.money) }); }
  };

  Builder.prototype.syncAfterpay = function (total) {
    var amount = (total / 100).toFixed(2);
    this.$all('afterpay-placement, square-placement, [data-afterpay-amount]').forEach(function (n) { n.setAttribute('data-amount', amount); });
  };

  Builder.prototype.stepState = function () {
    var s = this.state, t = this.t, self = this, fit = this.lastFit;
    this.steps.forEach(function (st) {
      var done = false, value = '';
      switch (st.key) {
        case 'colour': done = !!s.colour; value = s.colour || ''; break;
        case 'size': done = !!s.size; value = s.size || ''; break;
        case 'engraving':
          var n = self.engravingLines().length;
          done = n > 0 && fit && fit.level !== 'tiny';
          value = n === 1 ? t.lineOne : n ? fmt(t.lineMany, { n: n }) : '';
          break;
        case 'font': done = true; value = self.fontObj().label; break;
        case 'extras':
          done = true;
          var c = (s.packaging ? 1 : 0) + (s.card ? 1 : 0);
          value = c ? c + ' added' : t.optional;
          break;
      }
      st.el.classList.toggle('is-done', !!done);
      st.value.textContent = value;
      st.num.textContent = done ? '' : st.index;
      if (done && !st.num.firstChild) st.num.appendChild(self.icon('check'));
    });
  };

  Builder.prototype.clearErrors = function (keys) {
    var map = { colour: 'colour', size: 'size', text: 'engraving', confirmed: 'confirm' };
    var self = this;
    keys.forEach(function (k) {
      var key = map[k];
      if (!key) return;
      if (key === 'confirm') { if (self.confirmWrap) self.confirmWrap.classList.remove('is-error'); return; }
      self.steps.forEach(function (st) { if (st.key === key) { st.el.classList.remove('is-error'); st.msg.hidden = true; } });
    });
    this.atcMsg.hidden = true;
  };

  Builder.prototype.validate = function () {
    var s = this.state, t = this.t, v = this.variant(), errs = [];
    if (!s.colour) errs.push(['colour', t.needColour]);
    if (this.si >= 0 && !s.size) errs.push(['size', t.needSize]);
    var n = this.engravingLines().length;
    if (this.eng.required && !n) errs.push(['engraving', t.needText]);
    if (n && this.lastFit && this.lastFit.level === 'tiny') errs.push(['engraving', t.sizeTooSmall + '. ' + t.tooSmallHelp]);
    if ((this.si < 0 || s.size) && (!v || !v.available)) errs.push([this.si >= 0 ? 'size' : 'colour', t.unavailable]);
    if (this.confirmInput && !s.confirmed) errs.push(['confirm', t.needConfirm]);
    return errs;
  };

  Builder.prototype.showErrors = function (errs) {
    var self = this, first = null;
    errs.forEach(function (e) {
      if (e[0] === 'confirm') { self.confirmWrap.classList.add('is-error'); first = first || self.confirmWrap; return; }
      self.steps.forEach(function (st) {
        if (st.key === e[0]) { st.el.classList.add('is-error'); st.msg.textContent = e[1]; st.msg.hidden = false; first = first || st.el; }
      });
    });
    this.atcMsg.textContent = errs[0][1];
    this.atcMsg.hidden = false;
    if (first) {
      var y = first.getBoundingClientRect().top + window.pageYOffset - (this.cfg.scrollOffset || 110);
      window.scrollTo({ top: y, behavior: 'smooth' });
      if (first.getAttribute('data-step') === 'engraving') setTimeout(function () { try { self.textarea.focus({ preventScroll: true }); } catch (e) { /* ignore */ } }, 450);
    }
  };

  /* ------------------------------------------------------------------ *
   * Cart
   * ------------------------------------------------------------------ */
  Builder.prototype.buildItems = function () {
    var s = this.state, a = this.addons, e = this.eng;
    var v = this.variant();
    var link = 'AP-' + Date.now().toString(36).toUpperCase() + Math.random().toString(36).slice(2, 5).toUpperCase();
    var engr = this.engravingLines();
    var props = {};
    engr.forEach(function (l, i) { props[e.lineLabel + ' ' + (i + 1)] = l; });
    props[e.fontLabel] = this.fontObj().engrave;
    props._ap = link;
    props._engraving = engr.join('\n');
    var items = [{ id: v.id, quantity: 1, properties: props }];
    var who = {};
    who[this.t.forLabel] = (engr[0] ? engr[0] + ' \u00b7 ' : '') + this.cfg.product.title;
    if (s.packaging && a.packaging) items.push({ id: a.packaging.id, quantity: 1, properties: Object.assign({ _ap: link }, who) });
    if (s.card && a.card) items.push({ id: a.card.id, quantity: 1, properties: Object.assign({ _ap: link }, who) });
    // Shopify lists the newest line first, so the bracelet goes last and sits above its extras.
    return items.reverse();
  };

  Builder.prototype.addToCart = function () {
    var self = this, t = this.t;
    if (this.busy) return;
    var errs = this.validate();
    if (errs.length) { this.showErrors(errs); return; }
    var items = this.buildItems();
    var total = this.total();
    this.busy = true;
    this.atc.classList.add('is-busy');
    this.atc.querySelector('.apb-atc__label').textContent = t.adding;
    var done = function () {
      self.busy = false;
      self.atc.classList.remove('is-busy');
      self.atc.querySelector('.apb-atc__label').textContent = t.addToCart;
    };
    if (this.cfg.mock) {
      this.root.dispatchEvent(new CustomEvent('apb:mock-add', { bubbles: true, detail: { items: items, total: total } }));
      setTimeout(done, 300);
      return;
    }
    fetch(this.cfg.routes.cartAdd, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      return r.json().then(function (j) { return { ok: r.ok, json: j }; });
    }).then(function (res) {
      if (!res.ok) throw new Error((res.json && (res.json.description || res.json.message)) || t.error);
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'plate_builder_add_to_cart', value: total / 100, currency: self.cfg.currency, product: self.cfg.product.title, colour: self.state.colour, size: self.state.size, packaging: !!self.state.packaging, card: !!self.state.card });
      } catch (e) { /* ignore */ }
      self.root.dispatchEvent(new CustomEvent('apb:added', { bubbles: true, detail: { items: items, response: res.json } }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      if (self.cfg.afterAdd === 'cart' && !self.cfg.designMode) { window.location.href = self.cfg.routes.cart; return; }
      done();
      self.atcMsg.textContent = t.added;
      self.atcMsg.classList.add('is-ok');
      self.atcMsg.hidden = false;
      self.fetchCart();
    }).catch(function (err) {
      done();
      self.atcMsg.classList.remove('is-ok');
      self.atcMsg.textContent = err && err.message ? err.message : t.error;
      self.atcMsg.hidden = false;
    });
  };

  Builder.prototype.fetchCart = function () {
    var self = this, fs = this.cfg.freeShipping;
    if (this.cfg.mock || !fs || !fs.enabled || !window.fetch) return;
    fetch(this.cfg.routes.cartJs, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) { self.cartSubtotal = c.items_subtotal_price || c.total_price || 0; self.renderShipping(self.total()); })
      .catch(function () { /* ignore */ });
  };

  /* ------------------------------------------------------------------ *
   * Media: product photo for the chosen colour, gallery thumbs, zoom, URL
   * ------------------------------------------------------------------ */
  Builder.prototype.showColourPhoto = function () {
    var col = this.colourObj();
    if (col && col.photo && col.photo.src) this.showPhoto(col.photo.src, col.photo.alt);
  };

  Builder.prototype.showPhoto = function (src, alt) {
    var img = this.shotPhoto;
    if (!img || !src) return;
    if (img.getAttribute('src') !== src) {
      img.classList.add('is-swapping');
      img.onload = function () { img.classList.remove('is-swapping'); };
      img.setAttribute('src', src);
    }
    img.alt = alt || '';
    var active = null;
    this.$all('[data-apb-thumb]').forEach(function (b) {
      var on = b.getAttribute('data-src') === src;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on) active = b;
    });
    if (active) this.revealThumb(active);
    this.updateCount();
  };

  /* ---------------- Gallery: arrows, swipe, counter, thumbnails, zoom viewer ---------------- */
  Builder.prototype.buildGallery = function () {
    var self = this, t = this.t;
    var img = this.shotPhoto;
    this.gallery = this.$all('[data-apb-thumb]').map(function (b) {
      return { src: b.getAttribute('data-src'), zoom: b.getAttribute('data-zoom') || b.getAttribute('data-src'), alt: b.getAttribute('data-alt') || '' };
    });
    if (!this.gallery.length && img) this.gallery = [{ src: img.getAttribute('src'), zoom: img.getAttribute('data-zoom') || img.getAttribute('src'), alt: img.alt }];
    if (!this.shot || !img) return;
    var n = this.gallery.length;

    // tap or click the photo to open the zoom viewer
    this.shot.classList.add('is-zoomable');
    var zoomHint = h('button', { type: 'button', class: 'apb-shot__zoom', 'aria-label': t.galleryZoom, title: t.galleryZoom, onclick: function (ev) { ev.stopPropagation(); self.openViewer(); } }, [this.icon('expand')]);
    this.shot.appendChild(zoomHint);
    if (n > 1) {
      var nav = function (dir, label) {
        return h('button', { type: 'button', class: 'apb-shot__nav apb-shot__nav--' + (dir < 0 ? 'prev' : 'next'), 'aria-label': label,
          onclick: function (ev) { ev.stopPropagation(); self.stepPhoto(dir); } }, [self.icon(dir < 0 ? 'left' : 'right')]);
      };
      this.countEl2 = h('span', { class: 'apb-shot__count', 'aria-hidden': 'true' });
      append(this.shot, [nav(-1, t.galleryPrev), nav(1, t.galleryNext), this.countEl2]);
    }
    // swipe between photos on touch screens; a tap opens the viewer
    var sx = 0, sy = 0, moved = false, touch = false;
    this.shot.addEventListener('pointerdown', function (ev) { sx = ev.clientX; sy = ev.clientY; moved = false; touch = ev.pointerType !== 'mouse'; });
    this.shot.addEventListener('pointermove', function (ev) { if (Math.abs(ev.clientX - sx) > 8) moved = true; });
    this.shot.addEventListener('pointerup', function (ev) {
      var dx = ev.clientX - sx, dy = ev.clientY - sy;
      if (touch && n > 1 && Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) self.stepPhoto(dx < 0 ? 1 : -1);
    });
    img.addEventListener('click', function () { if (!moved) self.openViewer(); });
    img.style.touchAction = 'pan-y';

    // thumbnail strip: arrows instead of a scrollbar
    var wrap = this.$('[data-apb-thumbs-wrap]');
    if (wrap) {
      var strip = wrap.querySelector('.apb-thumbs');
      this.thumbStrip = strip;
      var vertical = function () { return window.getComputedStyle(strip).flexDirection === 'column'; };
      var move = function (dir) {
        var v = vertical();
        var amount = (v ? strip.clientHeight : strip.clientWidth) * 0.8 * dir;
        strip.scrollBy(v ? { top: amount, behavior: 'smooth' } : { left: amount, behavior: 'smooth' });
      };
      var prev = h('button', { type: 'button', class: 'apb-thumbs__nav apb-thumbs__nav--prev', 'aria-label': t.thumbsPrev, tabindex: '-1', onclick: function () { move(-1); } }, [this.icon('left')]);
      var next = h('button', { type: 'button', class: 'apb-thumbs__nav apb-thumbs__nav--next', 'aria-label': t.thumbsNext, tabindex: '-1', onclick: function () { move(1); } }, [this.icon('right')]);
      append(wrap, [prev, next]);
      var sync = function () {
        var v = vertical();
        var pos = v ? strip.scrollTop : strip.scrollLeft;
        var max = v ? strip.scrollHeight - strip.clientHeight : strip.scrollWidth - strip.clientWidth;
        wrap.classList.toggle('is-vertical', v);
        prev.hidden = pos <= 2;
        next.hidden = pos >= max - 2;
      };
      strip.addEventListener('scroll', function () { requestAnimationFrame(sync); }, { passive: true });
      window.addEventListener('resize', function () { requestAnimationFrame(sync); }, { passive: true });
      this.syncThumbs = sync;
      setTimeout(sync, 50);
      strip.querySelectorAll('img').forEach(function (im) { if (!im.complete) im.addEventListener('load', sync); });
    }
    this.updateCount();
  };

  Builder.prototype.photoIndex = function () {
    var src = this.shotPhoto ? this.shotPhoto.getAttribute('src') : '';
    for (var i = 0; i < this.gallery.length; i++) if (this.gallery[i].src === src) return i;
    return -1;
  };

  Builder.prototype.stepPhoto = function (dir) {
    var n = this.gallery.length;
    if (n < 2) return;
    var i = this.photoIndex();
    i = i < 0 ? 0 : (i + dir + n) % n;
    this.showPhoto(this.gallery[i].src, this.gallery[i].alt);
  };

  Builder.prototype.updateCount = function () {
    if (!this.countEl2) return;
    var i = this.photoIndex();
    this.countEl2.textContent = (i < 0 ? '' : (i + 1) + ' / ' + this.gallery.length);
    this.countEl2.hidden = i < 0;
  };

  // Scroll only the thumbnail strip (never the page) so the active thumb is in view
  Builder.prototype.revealThumb = function (btn) {
    var strip = this.thumbStrip;
    if (!strip) return;
    var v = window.getComputedStyle(strip).flexDirection === 'column';
    if (v) strip.scrollTo({ top: btn.offsetTop - strip.offsetTop - (strip.clientHeight - btn.offsetHeight) / 2, behavior: 'smooth' });
    else strip.scrollTo({ left: btn.offsetLeft - strip.offsetLeft - (strip.clientWidth - btn.offsetWidth) / 2, behavior: 'smooth' });
  };

  /* Full-screen viewer: big photo, arrows, tap or click to zoom in and look around */
  Builder.prototype.openViewer = function () {
    var self = this, t = this.t;
    if (!this.gallery.length) return;
    if (!this.viewer) {
      var img = h('img', { class: 'apb-lb__img', alt: '' });
      var stage = h('div', { class: 'apb-lb__stage' }, [img]);
      var count = h('span', { class: 'apb-lb__count' });
      var hint = h('span', { class: 'apb-lb__hint', text: t.viewerHint });
      var close = h('button', { type: 'button', class: 'apb-lb__btn apb-lb__close', 'aria-label': t.guideClose, onclick: function () { self.closeViewer(); } }, [
        sv('svg', { viewBox: '0 0 24 24', width: 22, height: 22, 'aria-hidden': 'true' }, [sv('path', { d: 'M6.4 5 5 6.4 10.6 12 5 17.6 6.4 19l5.6-5.6 5.6 5.6 1.4-1.4-5.6-5.6L19 6.4 17.6 5 12 10.6 6.4 5Z', fill: 'currentColor' })])
      ]);
      var prev = h('button', { type: 'button', class: 'apb-lb__btn apb-lb__nav apb-lb__nav--prev', 'aria-label': t.galleryPrev, onclick: function () { self.viewerStep(-1); } }, [this.icon('left')]);
      var next = h('button', { type: 'button', class: 'apb-lb__btn apb-lb__nav apb-lb__nav--next', 'aria-label': t.galleryNext, onclick: function () { self.viewerStep(1); } }, [this.icon('right')]);
      var box = h('div', { class: 'apb-lb', role: 'dialog', 'aria-modal': 'true', 'aria-label': this.cfg.product.title }, [stage, close, prev, next, count, hint]);
      box.hidden = true;
      document.body.appendChild(box);
      this.viewer = { box: box, stage: stage, img: img, count: count, prev: prev, next: next, hint: hint };

      // tap or click: zoom in on that spot, again to zoom out
      var downX = 0, downY = 0;
      stage.addEventListener('pointerdown', function (ev) { downX = ev.clientX; downY = ev.clientY; });
      stage.addEventListener('click', function (ev) {
        if (Math.abs(ev.clientX - downX) > 8 || Math.abs(ev.clientY - downY) > 8) return;
        if (ev.target !== img && !box.classList.contains('is-zoomed')) { self.closeViewer(); return; }
        self.viewerZoom(ev);
      });
      // desktop: when zoomed, the photo follows the mouse
      stage.addEventListener('mousemove', function (ev) {
        if (!box.classList.contains('is-zoomed')) return;
        var r = stage.getBoundingClientRect();
        stage.scrollLeft = (stage.scrollWidth - stage.clientWidth) * clamp((ev.clientX - r.left) / r.width, 0, 1);
        stage.scrollTop = (stage.scrollHeight - stage.clientHeight) * clamp((ev.clientY - r.top) / r.height, 0, 1);
      });
      // phone: swipe between photos when not zoomed
      var tx = 0, ty = 0;
      stage.addEventListener('touchstart', function (ev) { var p = ev.touches[0]; tx = p.clientX; ty = p.clientY; }, { passive: true });
      stage.addEventListener('touchend', function (ev) {
        if (box.classList.contains('is-zoomed')) return;
        var p = ev.changedTouches[0], dx = p.clientX - tx, dy = p.clientY - ty;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) self.viewerStep(dx < 0 ? 1 : -1);
      }, { passive: true });
      document.addEventListener('keydown', function (ev) {
        if (box.hidden) return;
        if (ev.key === 'Escape') self.closeViewer();
        else if (ev.key === 'ArrowRight') self.viewerStep(1);
        else if (ev.key === 'ArrowLeft') self.viewerStep(-1);
      });
    }
    var i = this.photoIndex();
    this.viewerIndex = i < 0 ? 0 : i;
    this._viewerFrom = document.activeElement;
    this.viewer.box.hidden = false;
    this.viewer.prev.hidden = this.viewer.next.hidden = this.gallery.length < 2;
    document.documentElement.classList.add('apb-noscroll');
    this.viewerShow();
    try { this.viewer.box.querySelector('.apb-lb__close').focus({ preventScroll: true }); } catch (e) { /* ignore */ }
  };

  Builder.prototype.viewerShow = function () {
    var v = this.viewer, g = this.gallery[this.viewerIndex];
    v.box.classList.remove('is-zoomed');
    v.img.src = g.zoom || g.src;
    v.img.alt = g.alt || '';
    v.count.textContent = this.gallery.length > 1 ? (this.viewerIndex + 1) + ' / ' + this.gallery.length : '';
  };

  Builder.prototype.viewerStep = function (dir) {
    var n = this.gallery.length;
    if (n < 2) return;
    this.viewerIndex = (this.viewerIndex + dir + n) % n;
    this.viewerShow();
  };

  Builder.prototype.viewerZoom = function (ev) {
    var v = this.viewer, stage = v.stage, img = v.img;
    if (v.box.classList.contains('is-zoomed')) { v.box.classList.remove('is-zoomed'); return; }
    var r = img.getBoundingClientRect();
    var fx = r.width ? clamp((ev.clientX - r.left) / r.width, 0, 1) : 0.5;
    var fy = r.height ? clamp((ev.clientY - r.top) / r.height, 0, 1) : 0.5;
    v.box.classList.add('is-zoomed');
    requestAnimationFrame(function () {
      stage.scrollLeft = (stage.scrollWidth - stage.clientWidth) * fx;
      stage.scrollTop = (stage.scrollHeight - stage.clientHeight) * fy;
    });
  };

  Builder.prototype.closeViewer = function () {
    if (!this.viewer || this.viewer.box.hidden) return;
    this.viewer.box.hidden = true;
    this.viewer.box.classList.remove('is-zoomed');
    document.documentElement.classList.remove('apb-noscroll');
    // show the photo they ended on
    var g = this.gallery[this.viewerIndex];
    if (g) this.showPhoto(g.src, g.alt);
    if (this._viewerFrom) try { this._viewerFrom.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
  };

  Builder.prototype.toggleZoom = function () {
    this.state.zoom = !this.state.zoom;
    if (this.stage) this.stage.classList.toggle('is-zoomed', this.state.zoom);
    this.zoomBtn.setAttribute('aria-pressed', this.state.zoom ? 'true' : 'false');
    this.zoomBtn.setAttribute('aria-label', this.state.zoom ? this.t.zoomOut : this.t.zoomIn);
    this.zoomBtn.title = this.state.zoom ? this.t.zoomOut : this.t.zoomIn;
    this.zoomBtn.innerHTML = '';
    this.zoomBtn.appendChild(this.icon(this.state.zoom ? 'unzoom' : 'zoom'));
    this.previews.forEach(function (p) { p.update(); });
  };

  Builder.prototype.syncUrl = function () {
    if (this.cfg.mock || !window.history || !history.replaceState) return;
    var v = this.variant();
    if (!v) return;
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      history.replaceState(history.state, '', url.toString());
    } catch (e) { /* ignore */ }
  };

  /* ------------------------------------------------------------------ *
   * Fonts
   * ------------------------------------------------------------------ */
  Builder.prototype.loadFont = function (font) {
    var self = this;
    if (!document.fonts || !document.fonts.load || !font) return;
    document.fonts.load('100px ' + font.css).then(function () { Measure.reset(); self.update(); }).catch(function () { /* ignore */ });
  };

  Builder.prototype.loadFonts = function () {
    var self = this;
    this.loadFont(this.fontObj());
    Object.keys(FACES).forEach(function (k) {
      var f = FACES[k];
      if (!f.local || f._probed || !window.FontFace) return;
      f._probed = true;
      var src = f.local.map(function (n) { return 'local("' + n + '")'; }).join(', ');
      try { new FontFace('apb-probe-' + k, src).load().then(function () { f._localOk = true; self.update(); }, function () { f._localOk = false; self.update(); }); }
      catch (e) { f._localOk = false; }
    });
    var warm = function () { self.fonts.forEach(function (f) { if (document.fonts && document.fonts.load) document.fonts.load('16px ' + f.css).catch(function () {}); }); };
    if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 2500 }); else setTimeout(warm, 1200);
    if (document.fonts && document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', function () { Measure.reset(); self.update(); });
  };

  /* ------------------------------------------------------------------ *
   * Preview: the back of the oval plate (SVG, units of 0.1 mm)
   * ------------------------------------------------------------------ */
  function Preview(b, mount, opts) {
    this.b = b;
    this.mount = mount;
    this.fixedZoom = !!opts.fixedZoom;
    var id = b.uid + '-' + (opts.suffix || 'p');
    this.id = id;
    var e = b.eng;
    var PW = e.plateW * U, PH = e.plateH * U;
    this.PW = PW; this.PH = PH;
    this.px = (STAGE.w * U - PW) / 2;
    this.py = (STAGE.h * U - PH) / 2;
    this.vb = this.fixedZoom ? this.zoomBox() : [0, 0, STAGE.w * U, STAGE.h * U];

    var svg = sv('svg', { class: 'apb-svg', viewBox: this.vb.join(' '), preserveAspectRatio: 'xMidYMid meet', role: 'img', 'aria-label': b.t.livePreview, focusable: 'false' });
    var defs = sv('defs');
    this.plateStops = [];
    var pg = sv('linearGradient', { id: id + '-pg', x1: 0, y1: 0, x2: 0.35, y2: 1 });
    [0, 0.45, 1].forEach(function (o) { var st = sv('stop', { offset: o }); pg.appendChild(st); this.plateStops.push(st); }, this);
    var sheen = sv('linearGradient', { id: id + '-sh', x1: 0, y1: 0, x2: 1, y2: 1 }, [
      sv('stop', { offset: 0.2, 'stop-color': '#fff', 'stop-opacity': 0 }),
      sv('stop', { offset: 0.42, 'stop-color': '#fff', 'stop-opacity': 0.4 }),
      sv('stop', { offset: 0.65, 'stop-color': '#fff', 'stop-opacity': 0 })
    ]);
    var drop = sv('filter', { id: id + '-ds', x: '-15%', y: '-25%', width: '130%', height: '160%' }, [
      sv('feDropShadow', { dx: 0, dy: 4, stdDeviation: 5, 'flood-color': '#3a2c22', 'flood-opacity': 0.22 })
    ]);
    // Text never spills past the oval
    var a = PW / 2, bb = PH / 2;
    var textClip = sv('clipPath', { id: id + '-tc' }, [sv('ellipse', { cx: a, cy: bb, rx: a - 4, ry: bb - 4 })]);
    append(defs, [pg, sheen, drop, textClip]);
    svg.appendChild(defs);

    this.gPlate = sv('g', { transform: 'translate(' + this.px + ' ' + this.py + ')' });
    this.plateArt = sv('g', { filter: 'url(#' + id + '-ds)' });
    var iw = PW / (1 - 2 * IMG_INSET.x), ih = PH / (1 - 2 * IMG_INSET.y);
    this.plateImg = sv('image', { x: -(iw - PW) / 2, y: -(ih - PH) / 2, width: iw, height: ih, preserveAspectRatio: 'none' });
    this.plateFallback = this.drawPlate(id);
    append(this.plateArt, [this.plateFallback, this.plateImg]);
    this.gEngr = sv('g', { 'clip-path': 'url(#' + id + '-tc)' });
    this.gHl = sv('g', {});
    this.gInk = sv('g', {});
    append(this.gEngr, [this.gHl, this.gInk]);
    append(this.gPlate, [this.plateArt, this.gEngr]);
    svg.appendChild(this.gPlate);
    this.svg = svg;
    mount.appendChild(svg);
    this.imgState = {};
  }

  // Drawn plate (used until / unless the plate photo loads): oval with two D-shaped chain slots
  Preview.prototype.drawPlate = function (id) {
    var PW = this.PW, PH = this.PH, a = PW / 2, b = PH / 2;
    var xs = SLOT.edge * PW, rx = SLOT.rx * PW, ry = SLOT.ry * PH;
    var oval = 'M0 ' + b + ' A' + a + ' ' + b + ' 0 1 0 ' + PW + ' ' + b + ' A' + a + ' ' + b + ' 0 1 0 0 ' + b + ' Z';
    var left = 'M' + xs + ' ' + (b - ry) + ' A' + rx + ' ' + ry + ' 0 0 0 ' + xs + ' ' + (b + ry) + ' Z';
    var right = 'M' + (PW - xs) + ' ' + (b - ry) + ' A' + rx + ' ' + ry + ' 0 0 1 ' + (PW - xs) + ' ' + (b + ry) + ' Z';
    var d = oval + ' ' + left + ' ' + right;
    return sv('g', {}, [
      sv('path', { d: d, 'fill-rule': 'evenodd', fill: 'url(#' + id + '-pg)' }),
      sv('path', { d: d, 'fill-rule': 'evenodd', fill: 'url(#' + id + '-sh)' })
    ]);
  };

  Preview.prototype.zoomBox = function () {
    var e = this.b.eng;
    var cx = STAGE.w * U / 2, cy = STAGE.h * U / 2;
    var w, hgt;
    if (this.fixedZoom) { w = (e.plateW + 4) * U; hgt = w / 2; }
    else { var aspect = STAGE.w / STAGE.h; w = Math.max(e.lineW + 6, (e.plateH + 1) * aspect) * U; hgt = w / aspect; }
    return [cx - w / 2, cy - hgt / 2, w, hgt];
  };

  Preview.prototype.setImage = function (img, url, fallback) {
    var self = this;
    if (!url || this.imgState[url] === 'error') { img.style.display = 'none'; fallback.style.display = ''; return; }
    if (img.getAttribute('href') !== url) {
      img.setAttribute('href', url);
      if (!this.imgState[url]) {
        var probe = new Image();
        probe.onload = function () { self.imgState[url] = 'ok'; };
        probe.onerror = function () { self.imgState[url] = 'error'; img.style.display = 'none'; fallback.style.display = ''; };
        probe.src = url;
        this.imgState[url] = 'loading';
      }
    }
    img.style.display = '';
    fallback.style.display = 'none';
  };

  Preview.prototype.update = function () {
    var b = this.b, col = b.colourObj();
    if (!col) return;
    this.plateStops[0].setAttribute('stop-color', col.light);
    this.plateStops[1].setAttribute('stop-color', mix(col.light, col.dark, 0.3));
    this.plateStops[2].setAttribute('stop-color', col.dark);
    this.setImage(this.plateImg, col.plate, this.plateFallback);
    this.drawText(col);
    var target = (this.fixedZoom || b.state.zoom) ? this.zoomBox() : [0, 0, STAGE.w * U, STAGE.h * U];
    this.animateTo(target);
  };

  Preview.prototype.drawText = function (col) {
    var b = this.b, e = b.eng;
    var real = b.engravingLines();
    var isExample = !real.length;
    var ls = isExample ? lines(e.placeholder) : real;
    this.gHl.innerHTML = '';
    this.gInk.innerHTML = '';
    if (!ls.length) return;
    var font = b.fontObj();
    var fit = isExample ? b.fit(ls, font) : b.lastFit;
    if (!fit) return;
    var light = luminance(col.text) > 0.4;
    var ink = col.text;
    var fsU = fit.fs * U, lhU = fit.lh * fsU;
    var cx = this.PW / 2, cy = this.PH / 2;
    var n = ls.length;
    var face = font.face || {};
    var stroke = face.twinStroke && !face._localOk ? face.twinStroke * fsU : 0;
    var self = this;
    this.gInk.setAttribute('fill', ink);
    this.gInk.style.mixBlendMode = light ? 'screen' : 'multiply';
    this.gInk.setAttribute('opacity', isExample ? 0.32 : 0.92);
    this.gHl.setAttribute('fill', light ? '#000' : '#fff');
    this.gHl.setAttribute('opacity', isExample ? 0 : 0.45);
    this.gHl.setAttribute('transform', 'translate(0 ' + (light ? -0.5 : 0.55) + ')');
    ls.forEach(function (l, i) {
      var y = cy + (i - (n - 1) / 2) * lhU + (fit.capRatio * fsU) / 2;
      [self.gHl, self.gInk].forEach(function (g) {
        var tx = sv('text', { x: cx, y: y, 'text-anchor': 'middle', 'font-size': fsU.toFixed(2), style: 'font-family:' + font.stack + ';white-space:pre' });
        tx.setAttributeNS(XMLNS, 'xml:space', 'preserve');
        if (stroke) { tx.setAttribute('stroke', g === self.gInk ? ink : (light ? '#000' : '#fff')); tx.setAttribute('stroke-width', stroke.toFixed(2)); tx.setAttribute('stroke-linejoin', 'round'); }
        tx.textContent = l;
        g.appendChild(tx);
      });
    });
  };

  Preview.prototype.animateTo = function (target) {
    var self = this;
    var from = this.vb.slice();
    if (from.every(function (v, i) { return Math.abs(v - target[i]) < 0.01; })) { this._painted = true; return; }
    cancelAnimationFrame(this._raf);
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !this._painted) { this.vb = target.slice(); this.svg.setAttribute('viewBox', this.vb.join(' ')); this._painted = true; return; }
    var t0 = performance.now(), dur = 420;
    var step = function (now) {
      var k = clamp((now - t0) / dur, 0, 1);
      var ease = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
      self.vb = from.map(function (v, i) { return v + (target[i] - v) * ease; });
      self.svg.setAttribute('viewBox', self.vb.join(' '));
      if (k < 1) self._raf = requestAnimationFrame(step);
    };
    this._raf = requestAnimationFrame(step);
  };

  /* ------------------------------------------------------------------ *
   * Description tabs: product descriptions often use a bold line such as
   * "Key Features:" or "Why Choose Auswara?" instead of a real heading. Each of
   * those starts its own tab. Long bold paragraphs (an intro) are left alone.
   * ------------------------------------------------------------------ */
  // Section names used across the Auswara catalogue (from a scan of all product descriptions)
  var KNOWN_HEADINGS = ['key features', 'features', 'product features', 'bracelet features', 'necklace features',
    'specifications', 'product specifications', 'product details', 'key details', 'why choose auswara', 'why auswara',
    'why choose us', 'why buy from auswara', 'why shop with us', 'ideal for', 'ideal gift for', 'perfect for',
    'perfect gift for', 'why you\u2019ll love it', 'why you\u2019ll love them', 'why choose this bracelet',
    'care instructions', 'sizing', 'sizing note', 'sizing details', 'personalised engraving'];
  function cleanHeading(t) {
    return String(t).replace(/\s+/g, ' ').replace(/'/g, '\u2019').replace(/^[\s\u2726*\u2022\-\u2013\u2014]+|[\s*:?.!]+$/g, '').trim().toLowerCase();
  }
  // A real heading, or a short line that is entirely bold and is either a known section name
  // or ends in ":" or "?" (e.g. "Key Features", "Why Choose Auswara?"). Taglines and notes
  // such as "***This Bracelet Contains Magnets***" are not headings.
  function headingText(el) {
    var txt = (el.textContent || '').replace(/\s+/g, ' ').trim();
    if (!txt) return null;
    if (/^H[2-4]$/.test(el.tagName)) return txt;
    if (txt.length > 60) return null;
    var isBoldOnly;
    if (el.tagName === 'STRONG' || el.tagName === 'B') isBoldOnly = true;
    else if (el.tagName === 'P') {
      var bold = Array.prototype.slice.call(el.querySelectorAll('strong, b')).map(function (b) { return b.textContent; }).join('').replace(/\s+/g, ' ').trim();
      isBoldOnly = bold === txt;
    } else return null;
    if (!isBoldOnly) return null;
    return (KNOWN_HEADINGS.indexOf(cleanHeading(txt)) > -1 || /[:?]$/.test(txt)) ? txt : null;
  }
  // Descriptions pasted in from elsewhere are often wrapped in a <div>: lift its contents up
  function unwrap(body) {
    for (var pass = 0; pass < 3; pass++) {
      var wrappers = Array.prototype.slice.call(body.children).filter(function (n) { return n.tagName === 'DIV' || n.tagName === 'SECTION'; });
      if (!wrappers.length) return;
      wrappers.forEach(function (w) { while (w.firstChild) body.insertBefore(w.firstChild, w); body.removeChild(w); });
    }
  }
  function splitDescription(scope) {
    (scope || document).querySelectorAll('details[data-apb-split]').forEach(function (det) {
      if (det.__split) return;
      det.__split = true;
      var body = det.querySelector('.apb-acc__body');
      if (!body) return;
      unwrap(body);
      var groups = [{ title: null, nodes: [] }];
      Array.prototype.slice.call(body.childNodes).forEach(function (n) {
        var ht = n.nodeType === 1 ? headingText(n) : null;
        if (ht) groups.push({ title: ht.replace(/^[\s\u2726*\u2022]+|[\s*]+$/g, '').replace(/:$/, ''), nodes: [] });
        else groups[groups.length - 1].nodes.push(n);
      });
      if (groups.length < 2) return;
      var hasText = function (g) { return g.nodes.some(function (n) { return (n.textContent || '').trim(); }); };
      var after = det;
      groups.slice(1).forEach(function (g) {
        if (!hasText(g)) return;
        var d = document.createElement('details');
        d.className = 'apb-acc';
        var sm = document.createElement('summary');
        sm.textContent = g.title;
        var b = document.createElement('div');
        b.className = 'apb-acc__body apb-rte';
        g.nodes.forEach(function (n) { b.appendChild(n); });
        d.appendChild(sm); d.appendChild(b);
        after.parentNode.insertBefore(d, after.nextSibling);
        after = d;
      });
      // the intro stays in the first tab; if there is no intro, that tab is removed
      body.innerHTML = '';
      groups[0].nodes.forEach(function (n) { body.appendChild(n); });
      if (!hasText(groups[0])) det.parentNode.removeChild(det);
    });
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */
  function boot(scope) {
    splitDescription(scope);
    (scope || document).querySelectorAll('[data-apb]').forEach(function (root) {
      if (root.__apb) return;
      try { root.__apb = new Builder(root); }
      catch (err) { if (window.console) console.error('[Auswara] plate builder failed to start', err); }
    });
  }
  window.AuswaraPlateBuilder = { boot: boot, Builder: Builder, formatMoney: formatMoney };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
